import {Button, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {type JSX, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {FieldValues, useFormContext} from 'react-hook-form';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {Form, FormProps} from '../input/Form';
import {ComponentWithoutChildren} from '../react/types';
import {ResponsiveDialog, ResponsiveDialogProps} from '../responsive/ResponsiveDialog';

type AllowedDialogProps = Omit<ResponsiveDialogProps, 'children' | 'onClose' | 'onInvalid' | 'onSubmit'>;

export interface DialogFormProps<TFieldValues extends FieldValues> extends FormProps<TFieldValues>, AllowedDialogProps {
  handleClose: () => void;
}

export interface DialogFormContextState {
  onAttemptClose: () => void;
}

const DialogFormContext = createContext<DialogFormContextState>({
  onAttemptClose: () => {
    // Default no-op
  },
});

export function useDialogForm(): DialogFormContextState {
  return useContext(DialogFormContext);
}

interface DirtyWatcherProps {
  onChange: (dirty: boolean) => void;
}

const DirtyWatcher: ComponentWithoutChildren<DirtyWatcherProps> = ({onChange}: DirtyWatcherProps) => {
  const {formState} = useFormContext();
  const isDirty = formState.isDirty;
  useEffect(() => {
    onChange(isDirty);
  }, [isDirty, onChange]);
  return null;
};

export function DialogForm<TFieldValues extends FieldValues>({
  children,
  defaultValues,
  delayError,
  handleClose,
  mode,
  onInvalid,
  onSubmit,
  open,
  reValidateMode,
  schema,
  ...dialogProps
}: DialogFormProps<TFieldValues>): JSX.Element {
  const [showConfirm, setShowConfirm] = useState(false);
  const isDirtyRef = useRef(false);
  const {translate} = useTranslation('common');

  const handleDirtyChange = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
  }, []);

  const onAttemptClose = useCallback(() => {
    if (isDirtyRef.current) {
      setShowConfirm(true);
    } else {
      handleClose();
    }
  }, [handleClose]);

  const handleCloseConfirm = useCallback(() => {
    setShowConfirm(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setShowConfirm(false);
    handleClose();
  }, [handleClose]);

  const contextValue = useMemo(
    () => ({
      onAttemptClose,
    }),
    [onAttemptClose],
  );

  return (
    <>
      <ResponsiveDialog {...dialogProps} onClose={onAttemptClose} open={open}>
        <Form
          defaultValues={defaultValues}
          delayError={delayError}
          mode={mode}
          onInvalid={onInvalid}
          onSubmit={onSubmit}
          reValidateMode={reValidateMode}
          schema={schema}
        >
          <DirtyWatcher onChange={handleDirtyChange} />
          <DialogFormContext.Provider value={contextValue}>{children}</DialogFormContext.Provider>
        </Form>
      </ResponsiveDialog>

      <ResponsiveDialog onClose={handleCloseConfirm} open={showConfirm}>
        <DialogTitle>{translate('common:dialog.unsavedChanges.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translate('common:dialog.unsavedChanges.content')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={handleCloseConfirm}>
            {translate('common:dialog.unsavedChanges.action.keep')}
          </Button>
          <Button color="error" onClick={handleDiscard}>
            {translate('common:dialog.unsavedChanges.action.discard')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
